import React, { useState } from 'react';
import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAbsenceApplication } from '@/components/AbsenceApplication/useAbsenceApplication';
import { EKlaseLogo } from '@/components/Logo';
import { LogoutConfirmDialog } from '@/components/LogoutConfirmDialog';
import { useProfileMenu } from '@/components/ProfileMenu/ProfileMenuProvider';
import { useProfileRestriction } from '@/components/ProfileRestriction/useProfileRestriction';
import { SidebarItem, sidebarItem } from '@/components/Sidebar/SidebarItem';
import { ThemeSwitcher } from '@/components/Sidebar/ThemeSwitcher';
import { useUserContext } from '@/auth/UserContext';
import { useUnseenEvents } from '@/hooks/useUnseenEvents';
import { ProfileMenuView } from '@/types/components';
import { PARENT_ROLES } from '@/types/user';
import {
  DiaryFilledIcon,
  DiaryIcon,
  HelpFilledIcon,
  HelpIcon,
  HomeFilledIcon,
  HomeIcon,
  LogoutIcon,
  MailFilledIcon,
  MailIcon,
  NotificationsIcon,
  PartnersFilledIcon,
  PartnersIcon,
  PlusIcon,
  SchoolLinksIcon,
  StatsFilledIcon,
  StatsIcon,
  SubscriptionIcon,
} from '@/components/Icons/SidebarIcons';
import type { NavItem } from '@/types/components';
import {
  ROUTE_DIARY,
  ROUTE_HOME,
  ROUTE_MAIL,
  ROUTE_OVERVIEW,
  ROUTE_PARTNERS,
  ROUTE_SCHOOL_LINKS,
  ROUTE_SUBSCRIPTION,
  ROUTE_SUPPORT,
} from '@/router/routes';
import { getMailTabCount } from '@/utils/mail';

export const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const { onOpen: openAbsenceApplication } = useAbsenceApplication();
  const { open: openProfileMenu } = useProfileMenu();
  const { data: unseenEvents } = useUnseenEvents({
    shouldRefetchOnMount: false,
  });
  const { isRestricted } = useProfileRestriction();
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  const canReportAbsence =
    !!user?.personType && PARENT_ROLES.has(user.personType);
  const hasUnseenMail = getMailTabCount(unseenEvents) > 0;

  const navItems: NavItem[] = [
    {
      to: ROUTE_HOME,
      label: t('sidebar.tabs.home'),
      icon: <HomeIcon className="size-6" />,
      iconActive: <HomeFilledIcon className="size-6" />,
      end: true,
    },
    {
      to: ROUTE_DIARY,
      label: t('sidebar.tabs.diary'),
      icon: <DiaryIcon className="size-6" />,
      iconActive: <DiaryFilledIcon className="size-6" />,
    },
    {
      to: ROUTE_OVERVIEW,
      label: t('sidebar.tabs.overview'),
      icon: <StatsIcon className="size-6" />,
      iconActive: <StatsFilledIcon className="size-6" />,
    },
    {
      to: ROUTE_MAIL,
      label: t('sidebar.tabs.mail'),
      icon: <MailIcon className="size-6" />,
      iconActive: <MailFilledIcon className="size-6" />,
    },
    {
      to: ROUTE_PARTNERS,
      label: t('sidebar.tabs.partners'),
      icon: <PartnersIcon className="size-6" />,
      iconActive: <PartnersFilledIcon className="size-6" />,
    },
    {
      to: ROUTE_SCHOOL_LINKS,
      label: t('sidebar.tabs.schoolLinks'),
      icon: <SchoolLinksIcon className="size-6" />,
    },
    {
      to: ROUTE_SUBSCRIPTION,
      label: t('sidebar.tabs.subscription'),
      icon: <SubscriptionIcon className="size-6" />,
    },
  ];

  const visibleNavItems = isRestricted
    ? navItems.filter((item) => item.to !== ROUTE_SUBSCRIPTION)
    : navItems;

  return (
    <aside
      aria-label={t('sidebar.aria.nav')}
      className="scrollbar-hidden hidden h-svh overflow-y-auto bg-card md:fixed md:inset-y-0 md:left-0 md:z-30 md:flex md:w-[92px] md:flex-col md:items-center lg:w-[250px] lg:items-stretch print:absolute! print:bottom-auto! print:h-auto! print:overflow-visible!"
    >
      <NavLink
        to={ROUTE_HOME}
        className="mb-5 flex h-[60px] w-full shrink-0 items-center justify-center lg:h-20"
      >
        <EKlaseLogo className="h-5 w-auto text-primary lg:h-10" />
      </NavLink>

      <nav className="flex w-full shrink-0 flex-col gap-2 px-3 lg:px-5">
        {visibleNavItems.map((item) => (
          <SidebarItem
            key={item.to}
            to={item.to}
            label={item.label}
            icon={item.icon}
            iconActive={item.iconActive}
            end={item.end}
            hasBadge={item.to === ROUTE_MAIL && hasUnseenMail}
          />
        ))}
        <button
          type="button"
          onClick={() => openProfileMenu(ProfileMenuView.Notifications)}
          className={`${sidebarItem({ isActive: false })} active:bg-info-surface active:text-primary contrast:active:bg-primary/10`}
        >
          <span className="flex size-6 shrink-0 items-center justify-center">
            <NotificationsIcon className="size-6" />
          </span>
          <span className="hidden flex-1 truncate text-left lg:inline">
            {t('sidebar.tabs.notifications')}
          </span>
        </button>
      </nav>

      <div className="mt-auto flex w-full shrink-0 flex-col items-center gap-3 px-3 pt-2 pb-1 lg:items-stretch lg:px-5">
        {canReportAbsence && (
          <button
            type="button"
            onClick={openAbsenceApplication}
            aria-label={t('global.reportAbsence')}
            className="flex size-10 cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary-hover lg:w-fit lg:px-4 lg:py-2"
          >
            <PlusIcon className="size-4 shrink-0 lg:size-5" />
            <span className="hidden text-callout-16 lg:inline">
              {t('global.reportAbsence')}
            </span>
          </button>
        )}

        <div className="flex w-full flex-col gap-2 border-t border-divider py-1">
          <ThemeSwitcher />
          <SidebarItem
            to={ROUTE_SUPPORT}
            label={t('global.help')}
            icon={<HelpIcon className="size-6" />}
            iconActive={<HelpFilledIcon className="size-6" />}
          />
          <button
            type="button"
            onClick={() => setIsLogoutConfirmOpen(true)}
            className={sidebarItem({ isActive: false })}
          >
            <span className="flex size-6 shrink-0 items-center justify-center">
              <LogoutIcon className="size-6" />
            </span>
            <span className="hidden flex-1 truncate text-left lg:inline">
              {t('global.logout')}
            </span>
          </button>
          <LogoutConfirmDialog
            isOpen={isLogoutConfirmOpen}
            onClose={() => setIsLogoutConfirmOpen(false)}
          />
        </div>
      </div>
    </aside>
  );
};
